import { AREAS, GRENADES, IMPORTANCES, STATUSES } from "@69ly/shared";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Filters, useFilters } from "../lib/filters.ts";
import { GrenadeIcon } from "./MarkerIcon.tsx";
import { Icon } from "./Icon.tsx";
import { ImportanceMark } from "./Importance.tsx";

type FilterState = ReturnType<typeof useFilters>;

/**
 * The map's filters, kept small: what players change all the time stays one click
 * away (grenade, side), the rest sits behind "Filters" with a count, and whatever
 * is set there shows as a chip that removes it. (Quick filters + a panel, the usual
 * pattern when a result view needs the space.) The Singles / Combos switch is not a
 * filter: the map view keeps it on the map, over its bottom edge.
 */
export function MapFilters({
  filters,
  update,
  clear,
  active,
  count,
  glass,
  authors = [],
}: FilterState & { count: number; glass?: boolean; /** Who wrote something on this map, for "Added by". */ authors?: { id: string; name: string }[] }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const toggle = (g: Filters["grenades"][number]) =>
    update({ grenades: filters.grenades.includes(g) ? filters.grenades.filter((x) => x !== g) : [...filters.grenades, g] });

  // What sits behind the button, as chips that remove themselves.
  const chips: { key: string; label: string; clear: () => void }[] = [];
  if (filters.area !== "all") chips.push({ key: "area", label: t(`area.${filters.area}`), clear: () => update({ area: "all" }) });
  if (filters.status !== "any") chips.push({ key: "status", label: t(`status.${filters.status}`), clear: () => update({ status: "any" }) });
  for (const i of filters.importance)
    chips.push({ key: `imp-${i}`, label: t(`importance.${i}`), clear: () => update({ importance: filters.importance.filter((x) => x !== i) }) });
  if (filters.q.trim()) chips.push({ key: "q", label: `“${filters.q.trim()}”`, clear: () => update({ q: "" }) });
  if (filters.by) {
    const name = authors.find((a) => a.id === filters.by)?.name ?? t("filters.someone");
    chips.push({ key: "by", label: t("filters.byChip", { name }), clear: () => update({ by: null }) });
  }

  return (
    <div className={`map-filters${glass ? " glass" : ""}`} ref={box} role="group" aria-label={t("mapView.filters")}>
      <div className="quick-grenades" role="group" aria-label={t("mapView.types")}>
        {GRENADES.map((g) => (
          <button
            key={g}
            type="button"
            className="quick-chip"
            aria-pressed={filters.grenades.includes(g)}
            aria-label={t(`grenade.${g}`)}
            title={t(`grenade.${g}`)}
            onClick={() => toggle(g)}
          >
            {/* In the side's colour when one side is shown, grey for both. */}
            <GrenadeIcon grenade={g} side={filters.side === "ct" ? "ct" : "t"} neutral={filters.side === "both"} size={24} />
          </button>
        ))}
      </div>
      <div className="segmented quick-side" role="group" aria-label={t("mapView.sideFilter")}>
        {(["both", "t", "ct"] as const).map((s) => (
          <button key={s} type="button" className="seg" aria-pressed={filters.side === s} onClick={() => update({ side: s })}>
            {s === "both" ? t("filters.allSides") : t(`sideShort.${s}`)}
          </button>
        ))}
      </div>
      <div className="filters-more">
        <button
          type="button"
          className={`btn btn-small filters-button${chips.length ? " has-active" : ""}`}
          aria-expanded={open}
          aria-haspopup="dialog"
          onClick={() => {
            setOpen(!open);
            setTimeout(() => search.current?.focus(), 0);
          }}
        >
          <Icon name="filter" size={14} />
          {t("filters.more")}
          {chips.length > 0 && <span className="filters-count">{chips.length}</span>}
        </button>
        {open && (
          <div className="filters-panel" role="dialog" aria-label={t("filters.more")}>
            <label className="field">
              <span>{t("mapView.search")}</span>
              <input ref={search} type="search" value={filters.q} placeholder={t("filters.searchHint")} onChange={(e) => update({ q: e.target.value })} />
            </label>
            <div className="field">
              <span>{t("mapView.areas")}</span>
              <div className="segmented" role="group" aria-label={t("mapView.areas")}>
                {(["all", ...AREAS] as const).map((a) => (
                  <button key={a} type="button" className="seg" aria-pressed={filters.area === a} onClick={() => update({ area: a })}>
                    {t(`areaShort.${a}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="field">
              <span>{t("importance.label")}</span>
              <div className="segmented" role="group" aria-label={t("importance.label")}>
                {IMPORTANCES.map((i) => (
                  <button
                    key={i}
                    type="button"
                    className="seg"
                    aria-pressed={filters.importance.includes(i)}
                    onClick={() => update({ importance: filters.importance.includes(i) ? filters.importance.filter((x) => x !== i) : [...filters.importance, i] })}
                  >
                    <ImportanceMark importance={i} />
                    {t(`importance.${i}`)}
                  </button>
                ))}
              </div>
            </div>
            {(authors.length > 1 || filters.by) && (
              <label className="field">
                <span>{t("filters.addedBy")}</span>
                <select value={filters.by ?? ""} onChange={(e) => update({ by: e.target.value || null })}>
                  <option value="">{t("filters.anyone")}</option>
                  {authors.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="field">
              <span>{t("mapView.myStatus")}</span>
              <div className="segmented" role="group" aria-label={t("mapView.myStatus")}>
                {(["any", ...STATUSES, "none"] as const).map((s) => (
                  <button key={s} type="button" className="seg" aria-pressed={filters.status === s} onClick={() => update({ status: s })}>
                    {t(`status.${s}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="filters-panel-foot">
              <button type="button" className="btn btn-quiet btn-small" disabled={!active} onClick={clear}>
                {t("filters.clearAll")}
              </button>
              <button type="button" className="btn btn-primary btn-small" onClick={() => setOpen(false)}>
                {t("filters.show", { count })}
              </button>
            </div>
          </div>
        )}
      </div>
      {chips.map((c) => (
        <button key={c.key} type="button" className="applied-chip" onClick={c.clear} aria-label={t("filters.remove", { filter: c.label })}>
          {c.label}
          <Icon name="close" size={12} />
        </button>
      ))}
      <span className="filters-shown small">{t("mapView.shown", { count })}</span>
    </div>
  );
}
